import axios, { CancelToken, CancelTokenSource } from 'axios'
import { MaybeRef } from '@vueuse/core'
import { onUnmounted, Ref, ref, unref } from 'vue'

import { storeToRefs } from 'pinia'
import { useSelectedTrip } from './trip'
import { useTransportationLocationDetails } from './transportation'
import { useLocationDetails } from './location'
import { useMutation } from './vue-query'
import { useContext } from '@/composables/context'
import { Location, LocationAutocompleteResult } from '@/types/trip'
import { TravelMode } from '@/utils/enums'
import {
  RouteAvoidOption,
  RouteSuggestion,
  TransportationUIInput,
} from '@/types/transportation'
import { parseAsLocalDate } from '@/utils/date-utils'
import { useTransportationStore } from '@/stores/transportation'
import {
  getTravelModeForTransportationType,
  createDirectRoute,
} from '@/utils/transportation'

export interface RouteManagerParams {
  /**
   * The locations to calculate the route for.
   * At least two locations are required to calculate a route.
   */
  locations: ({ latitude: number; longitude: number } | { placeId: string })[]
  /**
   * The travel mode option for the route calculation.
   * All travel modes are allowed except Direct.
   */
  travelMode: Exclude<TravelMode, 'direct'>
  /**
   * The departure date for the route calculation.
   * This is optional and can be used to calculate routes based on specific departure times.
   */
  departureDate?: string
  /**
   * Whether to include alternative routes in the calculation.
   * This is optional and defaults to false.
   */
  includeAlternatives?: boolean
  /**
   * The avoid options for the route calculation (driving only).
   */
  avoidOptions?: RouteAvoidOption[]
  /**
   * CancelToken to cancel the request.
   */
  cancelToken?: CancelToken
}

export function useCalculateRoute(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  return useMutation({
    mutationFn: async (params: RouteManagerParams) => {
      const { cancelToken, avoidOptions, ...requestParams } = params
      if (requestParams.locations.length < 2) {
        return []
      }

      const parsedDate = requestParams.departureDate
        ? parseAsLocalDate(requestParams.departureDate)
        : undefined

      const activeAvoidOptions =
        requestParams.travelMode === 'driving' && avoidOptions?.length
          ? avoidOptions
          : undefined

      const { data } = await $axios.post<{ routes: RouteSuggestion[] }>(
        `/api/trips/${unref(tripId)}/routes/calculate`,
        {
          ...requestParams,
          departureDate: parsedDate,
          ...(activeAvoidOptions ? { avoidOptions: activeAvoidOptions } : {}),
        },
        { cancelToken },
      )

      return data.routes
    },
  })
}

export function areSameLocation(location1: Location, location2: Location) {
  const latDiff = Math.abs(location1.latitude - location2.latitude)
  const lngDiff = Math.abs(location1.longitude - location2.longitude)

  const precision = 0.0001 // Precision for comparing coordinates

  return (
    location1.id === location2.id ||
    (location1.name === location2.name &&
      latDiff < precision &&
      lngDiff < precision)
  )
}

export function areSameLocationArrays(
  locations1: Location[],
  locations2: Location[],
) {
  if (locations1.length !== locations2.length) {
    return false
  }
  return locations2.every((location2, index) => {
    return areSameLocation(locations1[index], location2)
  })
}

export function useRouteManagement(transportation: Ref<TransportationUIInput>) {
  const trip = useSelectedTrip()
  const transportationStore = useTransportationStore()
  const { availableRoutes, isSaveDisabled } = storeToRefs(transportationStore)

  const { mutateAsync: calculateRoute } = useCalculateRoute(trip.value._id)
  const { mutateAsync: getLocationDetails } = useLocationDetails()
  const { getDetailsFromTransportation } = useTransportationLocationDetails()

  const lastOriginPlaceId = ref<string>()
  const lastDestinationPlaceId = ref<string>()
  const lastAvoidOptions = ref<RouteAvoidOption[]>()

  const isLoading = ref(false)
  let cancelTokenSource: CancelTokenSource | null = null

  /**
   * Cancels any pending route calculation.
   * This is called before starting a new calculation and on component unmount.
   */
  function cancelPendingCalculation() {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Calculation cancelled')
      isLoading.value = false
      isSaveDisabled.value = false
      cancelTokenSource = null
    }
  }

  onUnmounted(() => {
    cancelPendingCalculation()
  })

  /**
   * Calculates the routes based on the current transportation settings.
   * It performs multiple checks to ensure that calculation are only done when necessary:
   * - If the user has an active Pro subscription.
   * - If the origin and destination locations are valid and different.
   * - If the origin and destination haven't changed since the last calculation.
   * - If the transportation type is not 'direct', it calculates real routes.
   * - If the transportation type is 'direct', it calculates a direct route.
   *
   * It also updates the routes in the transportation store and manages the loading state.
   * @param params - The parameters for calculating the routes
   * @returns
   */
  async function calculateRoutes(params?: {
    includeAlternatives: boolean
    avoidOptions?: RouteAvoidOption[]
    resetSelectedRoute?: boolean
  }) {
    // Cancel any pending calculation before starting a new one
    cancelPendingCalculation()

    const origin = transportation.value.origin
    const destination = transportation.value.destination
    if (
      !origin ||
      !destination ||
      origin.location.placeId === destination.location.placeId
    ) {
      transportationStore.resetAvailableRoutes()
      lastOriginPlaceId.value = undefined
      lastDestinationPlaceId.value = undefined
      return
    } else if (
      lastOriginPlaceId.value === origin.location.placeId &&
      lastDestinationPlaceId.value === destination.location.placeId &&
      JSON.stringify(lastAvoidOptions.value) ===
        JSON.stringify(params?.avoidOptions) &&
      transportation.value.route &&
      (!params?.includeAlternatives || availableRoutes.value.length > 1)
    ) {
      // If the origin and destination haven't changed, avoid options haven't changed, and we already have routes, skip the calculation
      return
    }

    lastOriginPlaceId.value = origin.location.placeId
    lastDestinationPlaceId.value = destination.location.placeId
    lastAvoidOptions.value = params?.avoidOptions

    cancelTokenSource = axios.CancelToken.source()
    const currentSource = cancelTokenSource
    const cancelToken = cancelTokenSource.token

    isLoading.value = true
    isSaveDisabled.value = true

    const travelMode = getTravelModeForTransportationType(
      transportation.value.type,
    )

    try {
      if (!trip.value.activeProSubscription || travelMode === 'direct') {
        await calculateDirectRoute(origin, destination, cancelToken)
      } else {
        await calculateRealRoutes({
          origin,
          destination,
          travelMode,
          includeAlternatives: params?.includeAlternatives,
          avoidOptions: params?.avoidOptions,
          resetSelectedRoute: params?.resetSelectedRoute,
          cancelToken,
        })
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }
      throw error
    } finally {
      if (cancelTokenSource === currentSource) {
        isLoading.value = false
        isSaveDisabled.value = false
        cancelTokenSource = null
      }
    }
  }

  /**
   * Calculates the real routes between two locations.
   * This function retrieves the details of the origin and destination locations,
   * and then calculates the routes based on the travel mode.
   * @param params - The parameters for calculating the routes
   */
  async function calculateRealRoutes(params: {
    origin: LocationAutocompleteResult
    destination: LocationAutocompleteResult
    travelMode: Exclude<TravelMode, 'direct'>
    includeAlternatives?: boolean
    avoidOptions?: RouteAvoidOption[]
    resetSelectedRoute?: boolean
    cancelToken?: CancelToken
  }) {
    const getLocationOrPlaceId = (
      location: LocationAutocompleteResult,
    ): { latitude: number; longitude: number } | { placeId: string } => {
      if (location.dataSource === 'GOOGLE') {
        return { placeId: location.location.placeId }
      }
      return {
        latitude: location.location.latitude,
        longitude: location.location.longitude,
      }
    }

    const alternativeRoutes = await calculateRoute({
      locations: [
        getLocationOrPlaceId(params.origin),
        getLocationOrPlaceId(params.destination),
      ],
      travelMode: params.travelMode,
      departureDate: transportation.value.period?.start,
      includeAlternatives: params.includeAlternatives,
      avoidOptions: params.avoidOptions,
      cancelToken: params.cancelToken,
    })

    if (!alternativeRoutes.length) {
      transportationStore.resetAvailableRoutes()
      return
    }

    const currentRoute = transportation.value.route

    transportationStore.resetAvailableRoutes()
    availableRoutes.value.push(
      ...alternativeRoutes.map((alt) => ({
        ...alt,
        locations: alt.locations.map((location) => ({
          ...location,
        })),
        emoji: transportation.value!.emoji,
      })),
    )

    if (
      currentRoute &&
      currentRoute.travelMode !== 'direct' &&
      params.includeAlternatives &&
      !params.resetSelectedRoute
    ) {
      transportationStore.selectRoute({
        ...currentRoute,
        emoji: transportation.value.emoji,
      })
    } else {
      transportationStore.selectRoute(availableRoutes.value[0])
    }
  }

  /**
   * Creates a direct route between two locations.
   * This function retrieves the details of the origin and destination locations
   * @param origin - The origin location
   * @param destination - The destination location
   * @param cancelToken - CancelToken to cancel the request
   */
  async function calculateDirectRoute(
    origin: LocationAutocompleteResult,
    destination: LocationAutocompleteResult,
    cancelToken?: CancelToken,
  ) {
    // first, try to get the details from the transportation object, if it's
    // persisted
    let { originDetails, destinationDetails } = getDetailsFromTransportation(
      transportation.value,
    )

    if (!originDetails) {
      originDetails = await getLocationDetails({
        locationResult: origin,
        cancelToken,
      })
    }

    if (!destinationDetails) {
      destinationDetails = await getLocationDetails({
        locationResult: destination,
        cancelToken,
      })
    }

    const directRoute = createDirectRoute(originDetails, destinationDetails)
    transportationStore.resetAvailableRoutes()
    transportationStore.selectRoute({
      ...directRoute,
      emoji: transportation.value.emoji,
    })
  }

  return {
    isLoading,
    calculateRoutes,
    calculateDirectRoute,
    calculateRealRoutes,
  }
}
